import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { eventApi } from '@/services/api';
import './OnlineCoachingRegisterPage.css';

interface ScheduleDetails {
  id: string;
  title: string;
  category: string;
  dateLabel: string;
  timeLabel: string;
  durationLabel?: string;
  attendees?: number;
  instructor: string;
  description: string;
  learn: string[];
  requirements: string[];
  coverImage?: string;
  hostName?: string;
  hostExperience?: string;
  hostAvatar?: string;
}

// Cache buster for images
let pageLoadCacheBuster: string | null = null;

// Helper function to ensure image URLs load with proper CORS
const ensureImageUrl = (url: string | undefined | null): string => {
  if (!url || url === '/placeholder.svg') {
    return '/placeholder.svg';
  }
  
  if (url.includes('s3.ap-southeast-1.amazonaws.com') || url.includes('s3.amazonaws.com')) {
    const urlWithoutParams = url.split('?')[0];
    if (!pageLoadCacheBuster) {
      pageLoadCacheBuster = Date.now().toString();
    }
    return `${urlWithoutParams}?v=${pageLoadCacheBuster}`;
  }
  
  return url;
};

// Helper function to format date and time
const formatDate = (dateString: string): { dateLabel: string; timeLabel: string } => {
  const date = new Date(dateString);
  const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeLabel = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return { dateLabel, timeLabel };
};

// Helper function to format duration
const formatDuration = (minutes?: number): string | undefined => {
  if (!minutes) return undefined;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return `${hours}hr${hours > 1 ? 's' : ''} ${mins}min${mins > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours}hr${hours > 1 ? 's' : ''}`;
  } else {
    return `${mins}min${mins > 1 ? 's' : ''}`;
  }
};

// Helper function to format duration for display (e.g., "1hr 30mins", "2hrs")
const formatDurationDisplay = (minutes: number | undefined): string => {
  if (!minutes) return '1hr';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins}mins`;
  } else if (mins === 0) {
    return `${hours}hr${hours > 1 ? 's' : ''}`;
  } else {
    return `${hours}hr ${mins}mins`;
  }
};

// Helper function to determine if a session is ongoing
// A session is ongoing if current time is between schedule_at and schedule_at + duration
const isSessionOngoing = (scheduleAt: string | undefined, duration: number | undefined): boolean => {
  if (!scheduleAt) return false;
  
  const now = new Date();
  const startTime = new Date(scheduleAt);
  const endTime = duration 
    ? new Date(startTime.getTime() + duration * 60 * 1000) // duration is in minutes, convert to milliseconds
    : new Date(startTime.getTime() + 60 * 60 * 1000); // Default to 1 hour if duration not provided
  
  return now >= startTime && now <= endTime;
};

const OnlineCoachingRegisterPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<ScheduleDetails | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'instructor'>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<any>(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isExperienceExpanded, setIsExperienceExpanded] = useState(false);
  const [isExperienceLong, setIsExperienceLong] = useState(false);
  const experienceRef = useRef<HTMLParagraphElement>(null);
  const [relatedSessions, setRelatedSessions] = useState<Array<{
    id: number;
    scheduleId: number;
    title: string;
    category: string;
    thumbnailUrl?: string;
    scheduleAt?: string;
    zoomDuration?: number;
    maxSeat?: number;
    registrations?: number;
  }>>([]);

  useEffect(() => {
    pageLoadCacheBuster = Date.now().toString();
  }, []);

  useEffect(() => {
    const fetchScheduleDetails = async () => {
      if (!sessionId) {
        setError('Schedule ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch events and find the schedule
        const response = await eventApi.getEvents({ 
          type: 'online talks',
          status: 'active',
          per_page: 100 
        });

        const data = response.data as {
          status: string;
          message: string;
          data: {
            data: Array<{
              id: number;
              title: string;
              description: string;
              type: string;
              schedules?: Array<{
                id: number;
                schedule_at: string;
                description?: string | null;
                learning_objectives?: string[] | null;
                requirements?: string[] | null;
                zoom_duration?: number;
                thumbnail_url?: string;
                banner_url?: string;
                host_profile_picture_url?: string | null;
                host_name?: string | null;
                host_experience?: string | null;
                registrations?: Array<{
                  status_code: string;
                }>;
              }>;
              creator?: {
                first_name: string;
                last_name?: string;
              };
            }>;
          };
        };

        if (data.status === 'success' && data.data?.data) {
          // Find the schedule across all events
          let foundSchedule: any = null;
          let foundEvent: any = null;

          for (const event of data.data.data) {
            if (event.schedules) {
              const schedule = event.schedules.find(s => s.id.toString() === sessionId);
              if (schedule) {
                foundSchedule = schedule;
                foundEvent = event;
                break;
              }
            }
          }

          if (foundSchedule && foundEvent) {
            // Format date and time
            const { dateLabel, timeLabel } = formatDate(foundSchedule.schedule_at);
            const durationLabel = formatDuration(foundSchedule.zoom_duration);

            // Calculate attendees
            const attendees = foundSchedule.registrations?.filter(
              (r: any) => r.status_code === 'registered'
            ).length;

            // Get instructor from host_name
            const instructor = foundSchedule.host_name || 'Instructor';

            // Determine category
            const categoryMap: Record<string, string> = {
              'online talks': 'Career Development',
              'prof networking': 'Professional Networking',
            };
            const category = categoryMap[foundEvent.type] || foundEvent.type;

            // Get cover image
            const coverImage = ensureImageUrl(
              foundSchedule.banner_url || foundSchedule.thumbnail_url || '/placeholder.svg'
            );

            setSession({
              id: foundSchedule.id.toString(),
              title: foundEvent.title,
              category,
              dateLabel,
              timeLabel,
              durationLabel,
              attendees: attendees || undefined,
              instructor,
              description: foundSchedule.description || foundEvent.description || '',
              learn: Array.isArray(foundSchedule.learning_objectives) 
                ? foundSchedule.learning_objectives 
                : [],
              requirements: Array.isArray(foundSchedule.requirements) 
                ? foundSchedule.requirements 
                : [],
              coverImage,
              hostName: foundSchedule.host_name || undefined,
              hostExperience: foundSchedule.host_experience || undefined,
              hostAvatar: ensureImageUrl(foundSchedule.host_profile_picture_url),
            });

            // Fetch related sessions (other online talks events)
            const relatedEvents = data.data.data
              .flatMap((event) => {
                // Map each event's schedules to individual session items
                if (!event.schedules || event.schedules.length === 0) return [];
                return event.schedules
                  .filter((schedule) => {
                    // Exclude the current schedule
                    return schedule.id.toString() !== sessionId;
                  })
                  .map((schedule) => ({
                    id: event.id,
                    scheduleId: schedule.id,
                    title: event.title,
                    category: categoryMap[event.type] || event.type,
                    thumbnailUrl: schedule.thumbnail_url || schedule.banner_url || undefined,
                    scheduleAt: schedule.schedule_at || undefined,
                    zoomDuration: schedule.zoom_duration || undefined,
                    maxSeat: undefined,
                    registrations: schedule.registrations?.length || 0,
                  }));
              })
              .slice(0, 3); // Get only 3 related sessions

            setRelatedSessions(relatedEvents);
          } else {
            setError('Schedule not found');
          }
        } else {
          setError('Failed to load schedule details');
        }
      } catch (err: any) {
        console.error('Error fetching schedule details:', err);
        setError(err.response?.data?.message || 'Failed to load schedule details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchScheduleDetails();
  }, [sessionId]);

  // Fetch user quotas and check registration status on component mount
  useEffect(() => {
    const fetchQuotas = async () => {
      try {
        const response = await eventApi.getMyQuotas();
        setQuotaInfo(response.data);
        console.log('User quotas:', response.data);
      } catch (err: any) {
        console.error('Error fetching quotas:', err);
      }
    };

    const checkRegistrationStatus = async () => {
      if (!sessionId) return;
      
      try {
        const response = await eventApi.checkRegistrationStatus(sessionId);
        const responseData = response.data as {
          status: string;
          message: string;
          data: {
            is_registered: boolean;
            registration?: any;
          };
        };
        
        if (responseData.status === 'success' && responseData.data) {
          setIsRegistered(responseData.data.is_registered || false);
        } else {
          setIsRegistered(false);
        }
      } catch (err: any) {
        console.error('Error checking registration status:', err);
        // If endpoint doesn't exist yet or fails, default to false
        setIsRegistered(false);
      }
    };

    fetchQuotas();
    checkRegistrationStatus();
  }, [sessionId]);

  // Check if instructor experience is long enough to need collapse
  useEffect(() => {
    if (!session?.hostExperience || activeTab !== 'instructor') return;

    // Use a timeout to ensure DOM is rendered before checking
    const timeoutId = setTimeout(() => {
      if (!experienceRef.current) return;

      const element = experienceRef.current;
      const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight) || 20;
      const maxHeight = lineHeight * 3; // 3 lines
      
      // Create a temporary element to measure full height without collapsed class
      const tempElement = element.cloneNode(true) as HTMLElement;
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.height = 'auto';
      tempElement.style.maxHeight = 'none';
      tempElement.classList.remove('collapsed');
      document.body.appendChild(tempElement);
      
      const fullHeight = tempElement.scrollHeight;
      document.body.removeChild(tempElement);
      
      if (fullHeight > maxHeight) {
        setIsExperienceLong(true);
      } else {
        setIsExperienceLong(false);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [session?.hostExperience, activeTab]);

  const handleRegister = async () => {
    if (!session || isRegistering) return;

    try {
      setIsRegistering(true);
      const response = await eventApi.registerForSchedule(session.id);
      console.log('Registration successful:', response.data);
      setIsRegistered(true);
      setIsRegistering(false);
    } catch (err: any) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to register. Please try again.';
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          textAlign: 'left',
        },
      });
      
      setIsRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!session || isRegistering) return;

    try {
      setIsRegistering(true);
      await eventApi.cancelRegistration(session.id);
      setIsRegistered(false);
      setIsRegistering(false);
    } catch (err: any) {
      console.error('Cancel registration error:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to cancel registration. Please try again.';
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          textAlign: 'left',
        },
      });
      
      setIsRegistering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="coaching-register-page">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading session details...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="coaching-register-page">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>{error || 'Session not found'}</p>
          <Link to="/online-coaching" className="register-button" style={{ marginTop: '1rem', display: 'inline-block' }}>
            Back to Sessions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="coaching-register-page">
      {/* Back to Sessions button */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={() => navigate('/online-coaching')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: '0.875rem',
            padding: '0.5rem 0',
            transition: 'color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#9ca3af';
          }}
        >
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 16 16" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: 'rotate(180deg)' }}
          >
            <path 
              d="M6 12L10 8L6 4" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          <span>Back to Sessions</span>
        </button>
      </div>

      <div className="register-page-content">
        {/* Left: Main Content */}
        <div className="register-main-content">
          {/* Top: Session Thumbnail */}
          <div className="session-thumbnail">
            <img 
              src={session.coverImage || '/background.png'} 
              alt={session.title}
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
            />
          </div>

          {/* Title and quick actions */}
          <div className="details-header">
            <h1 className="title">{session.title}</h1>
            <div className="chips">
              <span className="chip purple">{session.category}</span>
              {session.durationLabel && (
                <span className="chip">{session.durationLabel}</span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={`tab ${activeTab === 'instructor' ? 'active' : ''}`}
              onClick={() => setActiveTab('instructor')}
            >
              Instructor
            </button>
          </div>

          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <section className="about">
              <h2>About this Session</h2>
              <p className="summary">{session.description}</p>

              <div className="cols">
                <div className="col">
                  <h3>What you'll learn</h3>
                  {session.learn.length > 0 ? (
                    <ul>
                      {session.learn.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ marginTop: '0.5rem' }}>No learning objectives specified.</p>
                  )}
                </div>
                <div className="col">
                  <h3>Requirements</h3>
                  {session.requirements.length > 0 ? (
                    <ul>
                      {session.requirements.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ marginTop: '0.5rem' }}>No requirements specified.</p>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Instructor Tab Content */}
          {activeTab === 'instructor' && (
            <section className="about">
              <h2>Instructor</h2>
              {session.hostName ? (
                <div className="instructor-card">
                  <img 
                    src={session.hostAvatar || '/placeholder.svg'} 
                    alt={session.hostName}
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                  />
                  <div className="instructor-card-content">
                    <h3 className="instructor-card-name">
                      {session.hostName}
                    </h3>
                    {session.hostExperience && (
                      <div className="instructor-card-experience-wrapper">
                        <p 
                          ref={experienceRef}
                          className={`instructor-card-experience ${!isExperienceExpanded && isExperienceLong ? 'collapsed' : ''}`}
                        >
                          {session.hostExperience}
                        </p>
                        {isExperienceLong && (
                          <button
                            type="button"
                            className="instructor-card-toggle"
                            onClick={() => setIsExperienceExpanded(!isExperienceExpanded)}
                          >
                            {isExperienceExpanded ? 'Show less' : 'Read more'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p>Instructor information not available.</p>
              )}
            </section>
          )}

          {/* Related Sessions */}
          <section className="related">
            <div className="related-head">
              <h2>Related Sessions</h2>
              <button className="view-all" onClick={() => navigate('/online-coaching')}>
                View All
              </button>
            </div>
            <div className="related-list">
              {relatedSessions.length > 0 ? (
                relatedSessions.map((relatedSession) => {
                  // Format duration
                  const durationText = formatDurationDisplay(relatedSession.zoomDuration);

                  // Determine if session is ongoing or upcoming
                  const isOngoing = isSessionOngoing(relatedSession.scheduleAt, relatedSession.zoomDuration);
                  
                  // Navigate to join (details page) if ongoing, or register page if upcoming
                  const handleClick = () => {
                    if (isOngoing) {
                      navigate(`/online-coaching/join/${relatedSession.scheduleId}`);
                    } else {
                      navigate(`/online-coaching/register/${relatedSession.scheduleId}`);
                    }
                  };

                  return (
                    <div
                      className="related-item"
                      key={`${relatedSession.id}-${relatedSession.scheduleId}`}
                      onClick={handleClick}
                      style={{ cursor: 'pointer' }}
                    >
                      <div
                        className="thumb"
                        style={{
                          backgroundImage: relatedSession.thumbnailUrl
                            ? `url(${ensureImageUrl(relatedSession.thumbnailUrl)})`
                            : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                      <div className="meta">
                        <div className="category-tag">{relatedSession.category}</div>
                        <div className="name">{relatedSession.title}</div>
                        <div className="row">
                          {/* {relatedSession.registrations && relatedSession.registrations > 0 && (
                            <div className="meta-item">
                              <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              <span>{relatedSession.registrations}</span>
                            </div>
                          )} */}
                          {relatedSession.zoomDuration && (
                            <div className="meta-item">
                              <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                              <span>{durationText}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '1rem' }}>
                  No related sessions available
                </p>
              )}
            </div>
          </section>
        </div>

        {/* Right: Registration Box */}
        <div className="register-box">
          {isRegistered ? (
            <>
              {/* Success Modal Content */}
              <div className="registration-success">
                <div className="success-icon-container">
                  <svg
                    className="success-checkmark"
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="24" cy="24" r="24" fill="#22C55E" />
                    <path
                      d="M15 24L21 30L33 18"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3 className="success-title">You're Registered!</h3>
                <p className="success-message">
                  We'll send you a notification when the session is about to start.
                </p>
                <div className="success-divider"></div>
                <div className="success-session-details">
                  <div className="success-detail-row">
                    <span className="success-detail-label">Session Date</span>
                    <span className="success-detail-value">{session.dateLabel}</span>
                  </div>
                  <div className="success-detail-row">
                    <span className="success-detail-label">Session Time</span>
                    <span className="success-detail-value">{session.timeLabel}</span>
                  </div>
                  <div className="success-detail-row">
                    <span className="success-detail-label">Duration</span>
                    <span className="success-detail-value">{session.durationLabel}</span>
                  </div>
                </div>
                <button
                  className="cancel-registration-button"
                  onClick={handleCancelRegistration}
                  disabled={isRegistering}
                >
                  Cancel Registration
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="register-box-title">Register for Session</h3>
              <p className="register-box-subtitle">Get notified when the session is about to start</p>

              <div className="register-info">
                <div className="register-info-item">
                  <svg
                    className="register-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span>{session.dateLabel}</span>
                </div>
                <div className="register-info-item">
                  <svg
                    className="register-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 8v5l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span>{session.timeLabel} • {session.durationLabel}</span>
                </div>
              </div>

              <Link
                to={`/online-coaching/join/${session.id}`}
                className="register-button"
                onClick={(e) => {
                  e.preventDefault();
                  handleRegister();
                }}
                style={{ opacity: isRegistering ? 0.6 : 1, pointerEvents: isRegistering ? 'none' : 'auto' }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.73 21a2 2 0 0 1-3.46 0"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {isRegistering ? 'Registering...' : 'Register & Get Notified'}
              </Link>

              <p className="register-reminder">
                You'll receive a reminder 30 minutes before the session starts
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnlineCoachingRegisterPage;

